/**
 * Progress Indicator Component
 * 
 * Displays progress with percentage and optional labels.
 */
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface ProgressIndicatorProps {
  value: number;
  max?: number;
  label?: string;
  showPercentage?: boolean;
  showValues?: boolean;
  formatValue?: (value: number) => string;
  size?: 'sm' | 'md' | 'lg';
  colorScheme?: 'default' | 'success' | 'warning' | 'danger' | 'auto';
  className?: string;
  tooltipContent?: string;
}

const sizeClasses = {
  sm: 'h-1.5',
  md: 'h-2.5',
  lg: 'h-4',
};

function getAutoColor(percentage: number): string {
  if (percentage >= 100) return 'bg-emerald-500';
  if (percentage >= 75) return 'bg-blue-500';
  if (percentage >= 50) return 'bg-amber-500';
  if (percentage >= 25) return 'bg-orange-500';
  return 'bg-rose-500';
}

const colorSchemes = {
  default: 'bg-primary',
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  danger: 'bg-rose-500',
  auto: '', // handled dynamically
};

export function ProgressIndicator({
  value,
  max = 100,
  label,
  showPercentage = true,
  showValues = false,
  formatValue,
  size = 'md',
  colorScheme = 'auto',
  className,
  tooltipContent,
}: Readonly<ProgressIndicatorProps>) {
  const percentage = Math.min(Math.round((value / max) * 100), 100);
  const colorClass =
    colorScheme === 'auto' ? getAutoColor(percentage) : colorSchemes[colorScheme];

  const defaultFormat = (v: number) => v.toLocaleString('pt-BR');
  const format = formatValue || defaultFormat;

  const progressBar = (
    <div className={cn('w-full space-y-1', className)}>
      {(label || showPercentage || showValues) && (
        <div className="flex items-center justify-between text-sm">
          {label && <span className="text-muted-foreground">{label}</span>}
          <div className="flex items-center gap-2">
            {showValues && (
              <span className="text-muted-foreground">
                {format(value)} / {format(max)}
              </span>
            )}
            {showPercentage && (
              <span className="font-medium tabular-nums">{percentage}%</span>
            )}
          </div>
        </div>
      )}
      <div className={cn('bg-secondary rounded-full overflow-hidden', sizeClasses[size])}>
        <div
          className={cn('h-full transition-all duration-500 ease-out rounded-full', colorClass)}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );

  if (tooltipContent) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{progressBar}</TooltipTrigger>
          <TooltipContent>
            <p>{tooltipContent}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return progressBar;
}

export default ProgressIndicator;


